import {
  Alert,
  Box,
  Button,
  Checkbox,
  Code,
  CopyButton,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo, useState } from "react";
import { FiCheck, FiClock, FiCopy, FiDatabase, FiList, FiPlay } from "react-icons/fi";
import { DROFUS_INTERFACE_OCCURRENCE_SELECT } from "@multiconsult-group/multiplacer-core";
import {
  openapi,
  type PostFunctionsDrofusApiProbeTestApiArg,
  type PostFunctionsDrofusOccurrencesLogsTestApiArg,
  type PostFunctionsDrofusOccurrencesTestApiArg,
  type PostFunctionsDrofusItemsOptionsTestApiArg,
  type PostFunctionsDrofusOccurrencesOptionsTestApiArg,
} from "../../openapi";
import { useAppSelector } from "../app/hooks";

const MMI_CANDIDATE_SELECT =
  "id,occurrence_name,occurrence_classification_109_classification_entry_id_code,ce109_id_or_parents,ce109_code_or_parents,ce109_name_or_parents,ce109_value_or_parents,ce109_display_value_or_parents";
const SHARP_WITH_MMI_CANDIDATES_SELECT = Array.from(
  new Set(`${DROFUS_INTERFACE_OCCURRENCE_SELECT},${MMI_CANDIDATE_SELECT}`.split(","))
).join(",");

const DEFAULT_FILTER = "startswith(room_id_room_classification_103_classification_entry_id_code,'J')";
const DEFAULT_PROJECT_NO = 10216575;
const DEFAULT_OCCURRENCE_LOG_SELECT = "field,action,time,old_value,new_value,note,change_source,change_list,username,occurrence_id";
type ResponseSource = "occurrences" | "options" | "probe" | "logs";
type OptionsResource = "items" | "occurrences";
type ProbeTarget = PostFunctionsDrofusApiProbeTestApiArg["body"]["target"];
type ProbeMethod = PostFunctionsDrofusApiProbeTestApiArg["body"]["method"];

export const DrofusDebugPage: React.FC = () => {
  const activeProjectNo = useAppSelector((state) => state.design.projectNo);
  const [projectNo, setProjectNo] = useState<number | "">(
    Number.isFinite(activeProjectNo) && activeProjectNo > 0 ? activeProjectNo : DEFAULT_PROJECT_NO
  );
  const [select, setSelect] = useState(SHARP_WITH_MMI_CANDIDATES_SELECT);
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  const [top, setTop] = useState(5);
  const [sample, setSample] = useState(5);
  const [maxRows, setMaxRows] = useState(5);
  const [optionsResource, setOptionsResource] = useState<OptionsResource>("occurrences");
  const [optionsDepth, setOptionsDepth] = useState(2);
  const [probeTarget, setProbeTarget] = useState<ProbeTarget>("swagger");
  const [probeMethod, setProbeMethod] = useState<ProbeMethod>("GET");
  const [probePath, setProbePath] = useState("/swagger/v1/swagger.json");
  const [probeQuery, setProbeQuery] = useState("");
  const [probeSearch, setProbeSearch] = useState("log,history,audit,change,user");
  const [probeIncludeBody, setProbeIncludeBody] = useState(false);
  const [probeMaxBodyChars, setProbeMaxBodyChars] = useState(20000);
  const [occurrenceLogOccurrenceId, setOccurrenceLogOccurrenceId] = useState<number | "">(364928);
  const [occurrenceLogTop, setOccurrenceLogTop] = useState(10);
  const [occurrenceLogSelect, setOccurrenceLogSelect] = useState(DEFAULT_OCCURRENCE_LOG_SELECT);
  const [responseSource, setResponseSource] = useState<ResponseSource>("occurrences");
  const [queryArg, setQueryArg] =
    useState<PostFunctionsDrofusOccurrencesTestApiArg | null>(null);
  const [itemsOptionsQueryArg, setItemsOptionsQueryArg] =
    useState<PostFunctionsDrofusItemsOptionsTestApiArg | null>(null);
  const [occurrencesOptionsQueryArg, setOccurrencesOptionsQueryArg] =
    useState<PostFunctionsDrofusOccurrencesOptionsTestApiArg | null>(null);
  const [probeQueryArg, setProbeQueryArg] =
    useState<PostFunctionsDrofusApiProbeTestApiArg | null>(null);
  const [occurrenceLogsQueryArg, setOccurrenceLogsQueryArg] =
    useState<PostFunctionsDrofusOccurrencesLogsTestApiArg | null>(null);

  const canRun = typeof projectNo === "number" && projectNo > 0;
  const canRunOccurrenceLogs =
    canRun && typeof occurrenceLogOccurrenceId === "number" && occurrenceLogOccurrenceId > 0;
  const { data, error, isFetching, fulfilledTimeStamp } =
    openapi.usePostFunctionsDrofusOccurrencesTestQuery(queryArg ?? skipToken);
  const {
    data: itemsOptionsData,
    error: itemsOptionsError,
    isFetching: isFetchingItemsOptions,
    fulfilledTimeStamp: itemsOptionsFulfilledTimeStamp,
  } = openapi.usePostFunctionsDrofusItemsOptionsTestQuery(itemsOptionsQueryArg ?? skipToken);
  const {
    data: occurrencesOptionsData,
    error: occurrencesOptionsError,
    isFetching: isFetchingOccurrencesOptions,
    fulfilledTimeStamp: occurrencesOptionsFulfilledTimeStamp,
  } = openapi.usePostFunctionsDrofusOccurrencesOptionsTestQuery(occurrencesOptionsQueryArg ?? skipToken);
  const {
    data: probeData,
    error: probeError,
    isFetching: isFetchingProbe,
    fulfilledTimeStamp: probeFulfilledTimeStamp,
  } = openapi.usePostFunctionsDrofusApiProbeTestQuery(probeQueryArg ?? skipToken);
  const {
    data: occurrenceLogsData,
    error: occurrenceLogsError,
    isFetching: isFetchingOccurrenceLogs,
    fulfilledTimeStamp: occurrenceLogsFulfilledTimeStamp,
  } = openapi.usePostFunctionsDrofusOccurrencesLogsTestQuery(occurrenceLogsQueryArg ?? skipToken);

  const optionsData = optionsResource === "items" ? itemsOptionsData : occurrencesOptionsData;
  const optionsError = optionsResource === "items" ? itemsOptionsError : occurrencesOptionsError;
  const isFetchingOptions =
    optionsResource === "items" ? isFetchingItemsOptions : isFetchingOccurrencesOptions;
  const optionsFulfilledTimeStamp =
    optionsResource === "items" ? itemsOptionsFulfilledTimeStamp : occurrencesOptionsFulfilledTimeStamp;

  const responseText = useMemo(() => {
    const value =
      responseSource === "logs"
        ? occurrenceLogsData ?? occurrenceLogsError ?? null
        : responseSource === "probe"
          ? probeData ?? probeError ?? null
          : responseSource === "options"
            ? optionsData ?? optionsError ?? null
            : data ?? error ?? null;
    return value ? JSON.stringify(value, null, 2) : "";
  }, [
    data,
    error,
    occurrenceLogsData,
    occurrenceLogsError,
    optionsData,
    optionsError,
    probeData,
    probeError,
    responseSource,
  ]);

  const activeError =
    responseSource === "logs"
      ? occurrenceLogsError
      : responseSource === "probe"
        ? probeError
        : responseSource === "options"
          ? optionsError
          : error;
  const activeFulfilledTimeStamp =
    responseSource === "logs"
      ? occurrenceLogsFulfilledTimeStamp
      : responseSource === "probe"
        ? probeFulfilledTimeStamp
        : responseSource === "options"
          ? optionsFulfilledTimeStamp
          : fulfilledTimeStamp;

  const run = () => {
    if (!canRun) return;
    setResponseSource("occurrences");
    setQueryArg({
      body: {
        projectNo,
        top,
        sample,
        maxRows,
        ...(select.trim() ? { select: select.trim() } : {}),
        ...(filter.trim() ? { filter: filter.trim() } : {}),
      },
    });
  };

  const runOptions = () => {
    if (!canRun) return;
    setResponseSource("options");
    const body = {
      projectNo,
      depth: optionsDepth,
    };
    if (optionsResource === "items") {
      setItemsOptionsQueryArg({ body });
    } else {
      setOccurrencesOptionsQueryArg({ body });
    }
  };

  const runProbe = () => {
    if (!canRun) return;
    setResponseSource("probe");
    setProbeQueryArg({
      body: {
        projectNo,
        target: probeTarget,
        method: probeMethod,
        path: probePath,
        includeBody: probeIncludeBody,
        maxBodyChars: probeMaxBodyChars,
        ...(probeQuery.trim() ? { query: probeQuery.trim() } : {}),
        ...(probeSearch.trim() ? { search: probeSearch.trim() } : {}),
      },
    });
  };

  const runOccurrenceLogs = () => {
    if (!canRunOccurrenceLogs) return;
    setResponseSource("logs");
    setOccurrenceLogsQueryArg({
      body: {
        projectNo,
        occurrenceId: occurrenceLogOccurrenceId,
        top: occurrenceLogTop,
        ...(occurrenceLogSelect.trim() ? { select: occurrenceLogSelect.trim() } : {}),
      },
    });
  };

  return (
    <Box w="100%" h="100%" p="md" style={{ overflow: "auto" }}>
      <Stack gap="md" maw={1280}>
        <Group justify="space-between" align="flex-start">
          <Box>
            <Group gap="xs">
              <FiDatabase size={24} />
              <Title order={2}>dRofus debug</Title>
            </Group>
            <Text c="dimmed" size="sm">
              Testhenting av occurrences med valfri select og filter.
            </Text>
          </Box>
        </Group>

        <Tabs
          value={responseSource}
          onChange={(value) =>
            setResponseSource(
              value === "logs"
                ? "logs"
                : value === "probe"
                  ? "probe"
                  : value === "options"
                    ? "options"
                    : "occurrences"
            )
          }
        >
          <Tabs.List>
            <Tabs.Tab value="occurrences" leftSection={<FiDatabase size={14} />}>
              Förekomst-test
            </Tabs.Tab>
            <Tabs.Tab value="options" leftSection={<FiList size={14} />}>
              OPTIONS
            </Tabs.Tab>
            <Tabs.Tab value="probe" leftSection={<FiDatabase size={14} />}>
              API probe
            </Tabs.Tab>
            <Tabs.Tab value="logs" leftSection={<FiClock size={14} />}>
              Förekomstlogg
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="occurrences" pt="md">
            <Paper withBorder radius="sm" p="md">
              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <Text fw={700}>Testa dRofus occurrences</Text>
                  <Button
                    leftSection={<FiPlay size={16} />}
                    disabled={!canRun}
                    loading={isFetching}
                    onClick={run}
                  >
                    Kör
                  </Button>
                </Group>

                <Group grow align="flex-start">
                  <NumberInput
                    label="Project no"
                    value={projectNo}
                    min={1}
                    onChange={(value) =>
                      setProjectNo(typeof value === "number" && Number.isFinite(value) ? value : "")
                    }
                  />
                  <NumberInput
                    label="Top (sidstorlek)"
                    value={top}
                    min={1}
                    max={100}
                    onChange={(value) => setTop(Number(value) || 5)}
                  />
                  <NumberInput
                    label="Max rows"
                    value={maxRows}
                    min={1}
                    max={500}
                    onChange={(value) => setMaxRows(Number(value) || 5)}
                  />
                  <NumberInput
                    label="Sample"
                    value={sample}
                    min={1}
                    max={100}
                    onChange={(value) => setSample(Number(value) || 5)}
                  />
                </Group>

                <Textarea
                  label="$select"
                  description={`${select.length} tegn`}
                  minRows={3}
                  autosize
                  value={select}
                  onChange={(event) => setSelect(event.currentTarget.value)}
                />

                <TextInput
                  label="$filter"
                  value={filter}
                  onChange={(event) => setFilter(event.currentTarget.value)}
                />
              </Stack>
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="options" pt="md">
            <Paper withBorder radius="sm" p="md">
              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <Group gap="xs">
                    <FiList size={18} />
                    <Text fw={700}>Tillgängliga dRofus-fält</Text>
                  </Group>
                  <Button
                    leftSection={<FiPlay size={16} />}
                    disabled={!canRun}
                    loading={isFetchingOptions}
                    onClick={runOptions}
                  >
                    Hämta {optionsResource} OPTIONS
                  </Button>
                </Group>
                <Group grow align="flex-start">
                  <NumberInput
                    label="Project no"
                    value={projectNo}
                    min={1}
                    onChange={(value) =>
                      setProjectNo(typeof value === "number" && Number.isFinite(value) ? value : "")
                    }
                  />
                  <SegmentedControl
                    value={optionsResource}
                    data={[
                      { value: "occurrences", label: "Occurrences" },
                      { value: "items", label: "Items" },
                    ]}
                    onChange={(value) => setOptionsResource(value as OptionsResource)}
                  />
                  <NumberInput
                    label="Depth"
                    value={optionsDepth}
                    min={1}
                    max={5}
                    onChange={(value) => setOptionsDepth(Number(value) || 2)}
                  />
                </Group>
              </Stack>
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="probe" pt="md">
            <Paper withBorder radius="sm" p="md">
              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <Group gap="xs">
                    <FiDatabase size={18} />
                    <Text fw={700}>Testa dRofus API/Swagger</Text>
                  </Group>
                  <Button
                    leftSection={<FiPlay size={16} />}
                    disabled={!canRun}
                    loading={isFetchingProbe}
                    onClick={runProbe}
                  >
                    Kör probe
                  </Button>
                </Group>

                <Group grow align="flex-start">
                  <NumberInput
                    label="Project no"
                    value={projectNo}
                    min={1}
                    onChange={(value) =>
                      setProjectNo(typeof value === "number" && Number.isFinite(value) ? value : "")
                    }
                  />
                  <SegmentedControl
                    value={probeTarget}
                    data={[
                      { value: "swagger", label: "Swagger" },
                      { value: "project", label: "Project API" },
                    ]}
                    onChange={(value) => {
                      const nextTarget = value as ProbeTarget;
                      setProbeTarget(nextTarget);
                      setProbePath(nextTarget === "swagger" ? "/swagger/v1/swagger.json" : "/occurrences");
                    }}
                  />
                  <SegmentedControl
                    value={probeMethod}
                    data={[
                      { value: "GET", label: "GET" },
                      { value: "OPTIONS", label: "OPTIONS" },
                    ]}
                    onChange={(value) => setProbeMethod(value as ProbeMethod)}
                  />
                </Group>

                <TextInput
                  label="Path"
                  value={probePath}
                  onChange={(event) => setProbePath(event.currentTarget.value)}
                />
                <TextInput
                  label="Query string"
                  placeholder="$top=5&$select=id,name"
                  value={probeQuery}
                  onChange={(event) => setProbeQuery(event.currentTarget.value)}
                />
                <TextInput
                  label="Sök i JSON"
                  value={probeSearch}
                  onChange={(event) => setProbeSearch(event.currentTarget.value)}
                />
                <Group align="center">
                  <Checkbox
                    label="Ta med body om den ryms"
                    checked={probeIncludeBody}
                    onChange={(event) => setProbeIncludeBody(event.currentTarget.checked)}
                  />
                  <NumberInput
                    label="Max body chars"
                    value={probeMaxBodyChars}
                    min={1000}
                    max={200000}
                    step={1000}
                    w={180}
                    onChange={(value) => setProbeMaxBodyChars(Number(value) || 20000)}
                  />
                </Group>
              </Stack>
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="logs" pt="md">
            <Paper withBorder radius="sm" p="md">
              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <Group gap="xs">
                    <FiClock size={18} />
                    <Text fw={700}>Hämta dRofus-logg för förekomst</Text>
                  </Group>
                  <Button
                    leftSection={<FiPlay size={16} />}
                    disabled={!canRunOccurrenceLogs}
                    loading={isFetchingOccurrenceLogs}
                    onClick={runOccurrenceLogs}
                  >
                    Hämta logg
                  </Button>
                </Group>

                <Group grow align="flex-start">
                  <NumberInput
                    label="Project no"
                    value={projectNo}
                    min={1}
                    onChange={(value) =>
                      setProjectNo(typeof value === "number" && Number.isFinite(value) ? value : "")
                    }
                  />
                  <NumberInput
                    label="Occurrence ID"
                    value={occurrenceLogOccurrenceId}
                    min={1}
                    onChange={(value) =>
                      setOccurrenceLogOccurrenceId(
                        typeof value === "number" && Number.isFinite(value) ? value : ""
                      )
                    }
                  />
                  <NumberInput
                    label="Top"
                    value={occurrenceLogTop}
                    min={1}
                    max={100}
                    onChange={(value) => setOccurrenceLogTop(Number(value) || 10)}
                  />
                </Group>

                <TextInput
                  label="$select"
                  value={occurrenceLogSelect}
                  onChange={(event) => setOccurrenceLogSelect(event.currentTarget.value)}
                />
              </Stack>
            </Paper>
          </Tabs.Panel>
        </Tabs>

        {Boolean(activeError) && (
          <Alert color="red" variant="light" title="Kunde inte hämta från dRofus">
            Funktionen svarade med fel. Se JSON-rutan för detaljer.
          </Alert>
        )}

        <Paper withBorder radius="sm" p="md">
          <Group justify="space-between" mb="sm">
            <Text fw={700}>Svar</Text>
            <Group gap="xs">
              {activeFulfilledTimeStamp && (
                <Text size="xs" c="dimmed">
                  {new Date(activeFulfilledTimeStamp).toLocaleTimeString()}
                </Text>
              )}
              <CopyButton value={responseText}>
                {({ copied, copy }) => (
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
                    disabled={!responseText}
                    onClick={copy}
                  >
                    {copied ? "Kopierad" : "Kopiera"}
                  </Button>
                )}
              </CopyButton>
            </Group>
          </Group>
          <Code block style={{ minHeight: 360, maxHeight: "60dvh", overflow: "auto", whiteSpace: "pre-wrap" }}>
            {responseText || "Ingen körning ännu."}
          </Code>
        </Paper>
      </Stack>
    </Box>
  );
};
